import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { updateWialonUser } from "../services/wialonUsers";
import {
  getClienteServicioById,
  searchClienteServicio,
} from "../../leasecontrol/services/leasecontrolUsuarios.service";

// Requisitos exigidos por Wialon/LeaseControl: 8-100 caracteres, especial, número, mayúscula y minúscula.
const PASSWORD_HINT =
  "8–100 caracteres, con mayúscula, minúscula, número y carácter especial";

function validatePasswordChange(newPass) {
  const wants = !!(newPass || "").trim();
  if (!wants) return null;

  const pass = newPass.trim();

  if (pass.length < 8 || pass.length > 100) {
    return "La contraseña debe tener entre 8 y 100 caracteres.";
  }
  if (!/[!-/:-@[-`{-~]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 carácter especial.";
  }
  if (!/[0-9]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 número.";
  }
  if (!/[A-Z]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 letra mayúscula.";
  }
  if (!/[a-z]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 letra minúscula.";
  }

  return null;
}

const getUserName = (u) => u?.name || u?.nm || "";

const getUserEnabled = (u) => {
  if (typeof u?.enabled === "boolean") return u.enabled;

  // Preferimos el flag real de Wialon (bit 0x1 de "fl" = deshabilitado) sobre el
  // espejo local, que puede desincronizarse si el estado cambia fuera de esta app.
  const wialonFlags = u?.fl ?? u?.usuario?.fl;
  if (typeof wialonFlags === "number") return (wialonFlags & 1) === 0;

  const estado = String(u?.local?.estado || "").toLowerCase();
  if (estado) return estado === "activo";
  return true;
};

const getCsLabel = (opt) => {
  if (!opt) return "";
  const nom = (opt.nombre_cliente || opt.razon_social || "").trim();
  const desc = (opt.descripcion || "").trim();
  if (nom) return desc ? `${nom} - ${desc}` : nom;
  if (opt.cod_servicio_cliente != null) return `ServicioCliente #${opt.cod_servicio_cliente}`;
  return "";
};

export default function WialonUserEditDialog({ open, onClose, user, onUpdated, vinculaciones }) {
  // Cuando la vista que abre el diálogo ya sabe que este usuario Wialon está
  // vinculado a más de un cliente (cuenta compartida), pasa la lista completa acá —
  // el campo "Cliente / Servicio" deja de ser un selector para reasignar UNA
  // vinculación puntual (no tiene sentido cuando hay varias) y pasa a listarlas todas,
  // de solo lectura. Nombre/Activo/contraseña siguen editables tal cual: son del
  // usuario Wialon, no de una vinculación en particular.
  const mostrarVinculacionesFijas = Array.isArray(vinculaciones) && vinculaciones.length > 0;
  const [userId, setUserId] = useState("");
  const [editName, setEditName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [editCodServicio, setEditCodServicio] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [csInput, setCsInput] = useState("");
  const [csOptions, setCsOptions] = useState([]);
  const [csLoading, setCsLoading] = useState(false);
  const [csSelected, setCsSelected] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;

    const codServicio = user?.local?.cod_servicio_cliente;
    const localNombre = user?.local?.nombre_cliente || user?.local?.razon_social;
    const localDesc = user?.local?.descripcion;

    setErr("");
    setUserId(String(user?.id || ""));
    setEditName(getUserName(user));
    setEnabled(getUserEnabled(user));
    setEditCodServicio(String(codServicio ?? ""));
    setNewPassword("");
    setShowPassword(false);

    if (codServicio != null) {
      setCsSelected({
        cod_servicio_cliente: codServicio,
        nombre_cliente: localNombre || "",
        descripcion: localDesc || "",
      });
      setCsInput(localNombre || `ServicioCliente #${codServicio}`);
    } else {
      setCsSelected(null);
      setCsInput("");
    }

    setCsOptions([]);
  }, [open, user]);

  useEffect(() => {
    if (!open) return;

    const term = (csInput || "").trim();
    if (term.length > 0 && term.length < 2) {
      setCsOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setCsLoading(true);
        const resp = await searchClienteServicio({
          codServicio: 2,
          limit: 20,
          nombreCliente: term,
          estado: "Activo",
        });
        setCsOptions(Array.isArray(resp?.data) ? resp.data : []);
      } catch {
        setCsOptions([]);
      } finally {
        setCsLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [csInput, open]);

  useEffect(() => {
    if (!open) return;

    const id = Number(editCodServicio || csSelected?.cod_servicio_cliente);
    if (!id || (csSelected?.nombre_cliente || "").trim()) return;

    let cancelled = false;
    (async () => {
      try {
        setCsLoading(true);
        const resp = await getClienteServicioById(id);
        if (cancelled) return;

        const data = resp?.data || null;
        if (data?.cod_servicio_cliente) {
          setCsSelected(data);
          setCsInput(data.nombre_cliente || data.razon_social || `ServicioCliente #${id}`);
        }
      } catch {
        // Fallback silencioso: mantiene el codigo visible aunque no cargue el nombre.
      } finally {
        if (!cancelled) setCsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, editCodServicio, csSelected?.cod_servicio_cliente, csSelected?.nombre_cliente]);

  const pwdError = useMemo(() => validatePasswordChange(newPassword), [newPassword]);

  const payloadEdit = useMemo(() => {
    const p = {};

    if ((editName || "").trim() && (editName || "").trim() !== getUserName(user)) {
      p.name = editName.trim();
    }

    const originalCod = user?.local?.cod_servicio_cliente;
    if (editCodServicio && Number(editCodServicio) !== Number(originalCod)) {
      p.cod_servicio_cliente = Number(editCodServicio);
    }

    if (enabled !== getUserEnabled(user)) {
      p.enabled = enabled;
    }

    if ((newPassword || "").trim()) {
      p.newPassword = newPassword.trim();
      p.reset = true;
      p.oldPassword = "x";
    }

    return p;
  }, [editName, editCodServicio, enabled, newPassword, user]);

  const canSave = useMemo(() => {
    if (!userId || pwdError) return false;
    return Object.keys(payloadEdit).length > 0;
  }, [userId, pwdError, payloadEdit]);

  const handleSave = async () => {
    setErr("");
    if (!userId) {
      setErr("No hay ID de usuario Wialon.");
      return;
    }
    if (pwdError) {
      setErr(pwdError);
      return;
    }

    try {
      setLoading(true);
      const resp = await updateWialonUser(userId, payloadEdit);
      onUpdated?.(resp?.data, payloadEdit);
      onClose?.();
    } catch (e) {
      setErr(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e.message ||
          "Error actualizando usuario Wialon."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ "& .MuiDialog-paper": { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1, typography: "h6", fontWeight: 600 }}>
        Editar usuario Wialon
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5, pb: 2.5, px: 2.5 }}>
        <form autoComplete="off">
          {!!err && (
            <Alert severity="error" sx={{ mb: 2.5 }} variant="outlined">
              {err}
            </Alert>
          )}

          <Grid container spacing={1.25}>
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Informacion basica
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: enabled ? 600 : 400 }}>
                      {enabled ? "Activo" : "Inactivo"}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            </Grid>

            <Grid size={12} sx={{ my: 1 }}>
              <Divider />
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Cambio de contrasena (opcional)
              </Typography>
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Nueva contrasena"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                error={!!pwdError}
                helperText={pwdError || PASSWORD_HINT}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => setShowPassword((prev) => !prev)}
                        sx={{ minWidth: 0, p: 0.5 }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={12} sx={{ my: 1 }}>
              <Divider />
            </Grid>

            {mostrarVinculacionesFijas ? (
              <Grid size={12}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Cliente / Servicio — {vinculaciones.length} vinculado{vinculaciones.length === 1 ? "" : "s"}
                </Typography>
                <Box sx={{ maxHeight: 180, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <List dense disablePadding>
                    {vinculaciones.map((v) => (
                      <ListItem key={v.id_vinculacion} divider>
                        <ListItemText
                          primary={`${v.razon_social || v.cod_cliente || "-"}${v.descripcion ? ` - ${v.descripcion}` : ""}`}
                          secondary={`Servicio #${v.cod_servicio_cliente}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            ) : (
              <Grid size={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disabled
                  value={csSelected}
                  options={csOptions}
                  loading={csLoading}
                  inputValue={csInput}
                  onInputChange={(e, v) => setCsInput(v)}
                  onChange={(e, opt) => {
                    setCsSelected(opt);
                    setEditCodServicio(
                      opt?.cod_servicio_cliente ? String(opt.cod_servicio_cliente) : ""
                    );
                  }}
                  isOptionEqualToValue={(a, b) =>
                    Number(a?.cod_servicio_cliente) === Number(b?.cod_servicio_cliente)
                  }
                  getOptionLabel={getCsLabel}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Cliente / Servicio"
                      placeholder="Buscar por nombre..."
                      helperText={`Actual: ${editCodServicio || "-"}`}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {csLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={!canSave}
        >
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
